import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from 'support/helpers/analytics/analytics';
import { FilterValue, PropertyFilter, PropertyFilterType, SearchPropertyFilter } from 'support/types/filters';

import { CheckboxFilter } from './CheckboxFilter';
import { FilterBadges } from './components/FilterBadges/FilterBadges';
import { FiltersMenu } from './FiltersMenu/FiltersMenu';
import { SearchFilter } from './SearchFilter';

type FiltersProps = {
  propertyFilters: Array<PropertyFilter>;
  onFiltersChange: (filters: Record<string, string>) => void;
  filterValues: Array<FilterValue> | undefined;
  totalElements?: number | null;
  clearAllFilters: () => void;
  rightSlot?: ReactNode;
  // When provided, the filters menu shows a "Save as Default" action that persists the current filters.
  onSaveAsDefault?: () => void;
};

export function Filters({
  propertyFilters,
  filterValues,
  onFiltersChange,
  clearAllFilters,
  totalElements,
  rightSlot,
  onSaveAsDefault,
}: FiltersProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const formattedFilters = useMemo(() => formatValues(filterValues), [filterValues]);
  const search: SearchPropertyFilter | undefined = propertyFilters.find(isSearchFilterGuard);
  const checkboxFilters = propertyFilters.filter((filter) => filter.type === PropertyFilterType.checkbox);

  // Drive the dropdown from the actual active values (filterValues), the same source as the badges.
  // `propertyFilters` carries config base defaults (e.g. action_required='true') that getFilterConfig
  // re-applies when a param is absent, which would keep a removed filter "active" in the dropdown.
  const effectivePropertyFilters = useMemo(
    () => propertyFilters.map((filter) => ({ ...filter, value: formattedFilters[filter.key] ?? '' })),
    [propertyFilters, formattedFilters],
  );

  const badgesFilters: Array<FilterValue> = useMemo(() => {
    return Object.entries(formattedFilters)
      .map((filter) => {
        const propertyField = propertyFilters.find((propertyFilter) => propertyFilter.key === filter[0]);
        if (!propertyField) {
          return undefined;
        }
        if (
          [PropertyFilterType.search, PropertyFilterType.hidden, PropertyFilterType.checkbox].includes(
            propertyField.type,
          )
        ) {
          return undefined;
        }

        if (propertyField.dropDownOptions?.multiple) {
          return (filter[1] as string).split(',').map((value) => {
            // Check if this exact value exists in dropdown options (could contain semicolons for compound values)
            const matchingOption = propertyField.dropDownOptions?.dropdownValues.find(
              (dropdownValue) => dropdownValue.value === value,
            );

            return {
              key: filter[0],
              value: matchingOption?.value || value,
            };
          });
        }

        return {
          key: filter[0],
          value:
            propertyField.dropDownOptions?.dropdownValues.find((dropdownValue) => dropdownValue.value === filter[1])
              ?.value || filter[1],
        };
      })
      .flat()
      .filter(Boolean) as Array<FilterValue>;
  }, [formattedFilters, propertyFilters]);

  if (!propertyFilters.length) {
    return null;
  }

  const hasActiveFilters = badgesFilters.some((filter) => filter.value);

  return (
    <div className="grid gap-2">
      <div className="flex w-full flex-wrap items-center gap-4">
        {!!search && (
          <SearchFilter
            placeholder={
              typeof totalElements === 'number' && typeof search.placeholder === 'function'
                ? search.placeholder(totalElements)
                : typeof search.placeholder === 'string'
                  ? search.placeholder
                  : t('common:search.placeholder')
            }
            currentValue={filterValues?.find((filter) => filter.key === search.key)?.value || ''}
            filterKey={search.key}
            onChange={(filterKey, newValue) => onFiltersChange({ ...formattedFilters, [filterKey]: newValue })}
          />
        )}

        <FiltersMenu
          propertyFilters={effectivePropertyFilters}
          onChange={(filterKey, newValue) => onFiltersChange({ ...formattedFilters, [filterKey]: newValue })}
          clearAllFilters={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
          track={track}
          onSaveAsDefault={onSaveAsDefault}
        />

        {checkboxFilters.map((filter) => (
          <CheckboxFilter
            key={filter.key}
            label={getFilterDisplayLabel(filter)}
            currentValue={formattedFilters[filter.key] ?? ''}
            filterKey={filter.key}
            onChange={(filterKey, newValue) => onFiltersChange({ ...formattedFilters, [filterKey]: newValue })}
          />
        ))}

        {rightSlot}
      </div>

      <FilterBadges
        filters={badgesFilters}
        filtersConfig={propertyFilters}
        remove={(key, value) => {
          onFiltersChange(formatValues(removeValueFromAppliedFilters(effectivePropertyFilters, key, value)));
        }}
      />
    </div>
  );
}

const getFilterDisplayLabel = (filter: PropertyFilter): string | undefined =>
  filter.label ?? (typeof filter.placeholder === 'string' ? filter.placeholder : undefined);

const removeValueFromAppliedFilters = (
  propertyFilters: Array<PropertyFilter>,
  key: string,
  value: string,
): Array<PropertyFilter> =>
  propertyFilters.map((filter) => {
    if (key !== filter.key) {
      return filter;
    }

    if (filter.type === PropertyFilterType.dropdown && filter.dropDownOptions) {
      const newValue = filter.value
        .split(',')
        .filter((v) => v !== value)
        .join(',');

      return {
        ...filter,
        value:
          newValue ||
          (filter.dropDownOptions.defaultToFirstValue ? filter.dropDownOptions.dropdownValues[0].value : ''),
      };
    }

    return filter.type === PropertyFilterType.search || filter.type === PropertyFilterType.hidden
      ? filter
      : { ...filter, value: '' };
  });

const formatValues = (filterValues: Array<FilterValue> | undefined): Record<string, string> =>
  filterValues?.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}) || {};

const isSearchFilterGuard = (filter: PropertyFilter): filter is SearchPropertyFilter => {
  return filter.type === PropertyFilterType.search;
};
